<template>
  <div class="business-detail" v-loading="loading">
    <!-- 成交台账详情 -->
    <div v-if="businessType === 'CUSTOMER_DEAL_RECORD' && dealDetail" class="deal-detail">
      <el-descriptions :column="2" border>
        <el-descriptions-item label="客户姓名" :span="2">
          <span class="customer-name">{{ dealDetail.customerName || '-' }}</span>
        </el-descriptions-item>
        <el-descriptions-item label="成交类型">
          {{ dealDetail.dealTypeText || getDealTypeText(dealDetail.dealType) }}
        </el-descriptions-item>
        <el-descriptions-item label="成交日期">
          {{ formatDate(dealDetail.dealDate) }}
        </el-descriptions-item>
        <el-descriptions-item label="成交金额(元)">
          <span class="amount">¥{{ formatMoney(dealDetail.dealAmount) }}</span>
        </el-descriptions-item>
        <el-descriptions-item label="佣金比例">
          {{ dealDetail.commissionRate ? `${dealDetail.commissionRate}%` : '0%' }}
        </el-descriptions-item>
        <el-descriptions-item label="佣金金额(元)">
          <span class="commission">¥{{ formatMoney(dealDetail.commissionAmount) }}</span>
        </el-descriptions-item>
        <el-descriptions-item label="产品名称" v-if="dealDetail.productName">
          {{ dealDetail.productName }}
        </el-descriptions-item>
        <el-descriptions-item label="产品类别" v-if="dealDetail.productCategory">
          {{ dealDetail.productCategory }}
        </el-descriptions-item>
        <el-descriptions-item label="关联合同编号" v-if="dealDetail.contractNumber">
          {{ dealDetail.contractNumber }}
        </el-descriptions-item>
        <el-descriptions-item label="合同ID" v-if="dealDetail.contractId">
          {{ dealDetail.contractId }}
        </el-descriptions-item>
        <el-descriptions-item label="付款状态">
          <el-tag :type="getPaymentStatusTagType(dealDetail.paymentStatus)">
            {{ dealDetail.paymentStatusText || getPaymentStatusText(dealDetail.paymentStatus) }}
          </el-tag>
        </el-descriptions-item>
        <el-descriptions-item
          label="付款金额(元)"
          v-if="dealDetail.paymentStatus === 'partial' || dealDetail.paymentStatus === 'partially_paid'"
        >
          <span class="amount">¥{{ formatMoney(dealDetail.paymentAmount) }}</span>
        </el-descriptions-item>
        <el-descriptions-item label="付款日期" v-if="dealDetail.paymentDate">
          {{ formatDate(dealDetail.paymentDate) }}
        </el-descriptions-item>
        <el-descriptions-item label="发票编号" v-if="dealDetail.invoiceNumber">
          {{ dealDetail.invoiceNumber }}
        </el-descriptions-item>
        <el-descriptions-item label="状态" v-if="dealDetail.status">
          <el-tag :type="getStatusTagType(dealDetail.status)">
            {{ getDealStatusText(dealDetail.status) }}
          </el-tag>
        </el-descriptions-item>
        <el-descriptions-item label="业务员ID" v-if="dealDetail.salesmanId">
          {{ dealDetail.salesmanId }}
        </el-descriptions-item>
        <el-descriptions-item label="客户ID" v-if="dealDetail.customerId">
          {{ dealDetail.customerId }}
        </el-descriptions-item>
        <el-descriptions-item label="审批流程ID" v-if="dealDetail.approvalFlowId">
          {{ dealDetail.approvalFlowId }}
        </el-descriptions-item>
        <el-descriptions-item label="备注" v-if="dealDetail.remark" :span="2">
          {{ dealDetail.remark }}
        </el-descriptions-item>
        <el-descriptions-item label="创建人" v-if="dealDetail.createBy">
          {{ dealDetail.createBy }}
        </el-descriptions-item>
        <el-descriptions-item label="创建时间" v-if="dealDetail.createTime">
          {{ formatDateTime(dealDetail.createTime) }}
        </el-descriptions-item>
        <el-descriptions-item label="更新人" v-if="dealDetail.updateBy">
          {{ dealDetail.updateBy }}
        </el-descriptions-item>
        <el-descriptions-item label="更新时间" v-if="dealDetail.updateTime">
          {{ formatDateTime(dealDetail.updateTime) }}
        </el-descriptions-item>
      </el-descriptions>
      
      <!-- 成交台账附件（独立展示，避免在 el-descriptions 中导致 ResizeObserver 错误） -->
      <div v-if="dealAttachments && dealAttachments.length" class="deal-attachments-section">
        <h4 class="attachments-section-title">附件</h4>
        <div class="attachment-list">
          <!-- 图片附件：显示缩略图 -->
          <div
            v-for="(attachment, index) in dealAttachments"
            :key="index"
            :class="['attachment-item', { 'attachment-item-image': isAttachmentImage(attachment) }]"
          >
            <template v-if="isAttachmentImage(attachment)">
              <div class="attachment-image-wrapper">
                <el-image
                  :src="getAttachmentUrl(attachment)"
                  :alt="getAttachmentFileName(attachment)"
                  :preview-src-list="getDealImagePreviewList()"
                  :initial-index="getDealImageIndex(index)"
                  fit="cover"
                  class="attachment-image-preview"
                  :preview-teleported="true"
                  crossorigin="anonymous"
                  referrerpolicy="no-referrer-when-downgrade"
                  @error="handleImageError"
                >
                  <template #error>
                    <div class="image-slot">
                      <el-icon><Picture /></el-icon>
                    </div>
                  </template>
                </el-image>
                <!-- 下载按钮覆盖层 -->
                <div class="download-overlay">
                  <el-button
                    type="primary"
                    :icon="Download"
                    circle
                    size="small"
                    class="download-overlay-btn"
                    @click.stop="handleDownloadAttachment(attachment)"
                    title="下载图片"
                  />
                </div>
              </div>
              <div class="attachment-image-info">
                <div class="attachment-image-details">
                  <span class="attachment-name" :title="getAttachmentFileName(attachment)">
                    {{ getAttachmentFileName(attachment) }}
                  </span>
                  <span v-if="getAttachmentSize(attachment) > 0" class="attachment-size">
                    ({{ formatFileSize(getAttachmentSize(attachment)) }})
                  </span>
                </div>
              </div>
            </template>
            
            <!-- 非图片附件：显示图标 -->
            <template v-else>
              <div :class="['attachment-icon-wrapper', getFileIconClass(attachment)]">
                <el-icon class="attachment-icon">
                  <component :is="getFileIcon(attachment)" />
                </el-icon>
                <span v-if="getFileTypeLabel(attachment)" class="file-type-label">
                  {{ getFileTypeLabel(attachment) }}
                </span>
              </div>
              <span class="attachment-name" :title="getAttachmentFileName(attachment)">
                {{ getAttachmentFileName(attachment) }}
              </span>
              <span v-if="getAttachmentSize(attachment) > 0" class="attachment-size">
                ({{ formatFileSize(getAttachmentSize(attachment)) }})
              </span>
              <div class="attachment-actions">
                <el-button
                  type="primary"
                  size="small"
                  :icon="Download"
                  link
                  @click="handleDownloadAttachment(attachment)"
                >
                  下载
                </el-button>
              </div>
            </template>
          </div>
        </div>
      </div>
      <div v-else-if="dealDetail.attachments" class="deal-attachments-section">
        <h4 class="attachments-section-title">附件</h4>
        <span>无附件</span>
      </div>
    </div>

    <!-- 合同详情 -->
    <div v-else-if="businessType === 'CONTRACT' && contractDetail" class="contract-detail">
      <el-descriptions :column="2" border>
        <el-descriptions-item label="合同编号" :span="2">
          <span class="contract-number">{{ contractDetail.contractNumber || '-' }}</span>
        </el-descriptions-item>
        <el-descriptions-item label="客户姓名">
          {{ contractDetail.customerName || '-' }}
        </el-descriptions-item>
        <el-descriptions-item label="合同金额(元)">
          <span class="amount">¥{{ formatMoney(contractDetail.contractAmount) }}</span>
        </el-descriptions-item>
        <el-descriptions-item label="合同类型" v-if="contractDetail.contractType">
          {{ getContractTypeText(contractDetail.contractType) }}
        </el-descriptions-item>
        <el-descriptions-item label="合同状态" v-if="contractDetail.contractStatus">
          <el-tag :type="getContractStatusTagType(contractDetail.contractStatus)">
            {{ getContractStatusText(contractDetail.contractStatus) }}
          </el-tag>
        </el-descriptions-item>
        <el-descriptions-item label="开始日期" v-if="contractDetail.startDate">
          {{ formatDate(contractDetail.startDate) }}
        </el-descriptions-item>
        <el-descriptions-item label="结束日期" v-if="contractDetail.endDate">
          {{ formatDate(contractDetail.endDate) }}
        </el-descriptions-item>
        <el-descriptions-item label="业务员ID" v-if="contractDetail.salesmanId">
          {{ contractDetail.salesmanId }}
        </el-descriptions-item>
        <el-descriptions-item label="客户ID" v-if="contractDetail.customerId">
          {{ contractDetail.customerId }}
        </el-descriptions-item>
        <el-descriptions-item label="合同内容" v-if="contractDetail.content" :span="2">
          <div class="content-text">{{ contractDetail.content }}</div>
        </el-descriptions-item>
        <el-descriptions-item label="合同图片" v-if="contractDetail.contractImages && contractDetail.contractImages.length > 0" :span="2">
          <ImagePreview :model-value="contractDetail.contractImages" />
        </el-descriptions-item>
        <el-descriptions-item label="合同附件" v-if="contractAttachments && contractAttachments.length > 0" :span="2">
          <AttachmentUpload 
            :model-value="contractAttachments"
            :show-actions="false"
            :show-download="true"
            :show-tip="false"
            :show-add-button="false"
          />
        </el-descriptions-item>
        <el-descriptions-item label="起草人" v-if="contractDetail.draftBy">
          {{ contractDetail.draftBy }}
        </el-descriptions-item>
        <el-descriptions-item label="起草时间" v-if="contractDetail.draftTime">
          {{ formatDateTime(contractDetail.draftTime) }}
        </el-descriptions-item>
        <el-descriptions-item label="审核人" v-if="contractDetail.reviewBy">
          {{ contractDetail.reviewBy }}
        </el-descriptions-item>
        <el-descriptions-item label="审核时间" v-if="contractDetail.reviewTime">
          {{ formatDateTime(contractDetail.reviewTime) }}
        </el-descriptions-item>
        <el-descriptions-item label="备注" v-if="contractDetail.remark" :span="2">
          {{ contractDetail.remark }}
        </el-descriptions-item>
        <el-descriptions-item label="创建人" v-if="contractDetail.createBy">
          {{ contractDetail.createBy }}
        </el-descriptions-item>
        <el-descriptions-item label="创建时间" v-if="contractDetail.createTime">
          {{ formatDateTime(contractDetail.createTime) }}
        </el-descriptions-item>
        <el-descriptions-item label="更新人" v-if="contractDetail.updateBy">
          {{ contractDetail.updateBy }}
        </el-descriptions-item>
        <el-descriptions-item label="更新时间" v-if="contractDetail.updateTime">
          {{ formatDateTime(contractDetail.updateTime) }}
        </el-descriptions-item>
      </el-descriptions>
    </div>

    <!-- 返货单详情 -->
    <div v-else-if="businessType === 'RETURN_ORDER' && returnOrderDetail" class="return-order-detail">
      <el-descriptions :column="2" border>
        <el-descriptions-item label="返货单号" :span="2">
          <span class="order-number">{{ returnOrderDetail.returnOrderNo || returnOrderDetail.orderNumber || '-' }}</span>
        </el-descriptions-item>
        <el-descriptions-item label="客户姓名">
          {{ returnOrderDetail.customerName || '-' }}
        </el-descriptions-item>
        <el-descriptions-item label="返货日期" v-if="returnOrderDetail.returnDate">
          {{ formatDate(returnOrderDetail.returnDate) }}
        </el-descriptions-item>
        <el-descriptions-item label="返货类型" v-if="returnOrderDetail.returnType !== undefined && returnOrderDetail.returnType !== null">
          {{ getReturnTypeText(returnOrderDetail.returnType) }}
        </el-descriptions-item>
        <el-descriptions-item label="返货原因" v-if="returnOrderDetail.returnReason" :span="2">
          {{ returnOrderDetail.returnReason }}
        </el-descriptions-item>
        <el-descriptions-item label="原订单号" v-if="returnOrderDetail.originalOrderNo">
          {{ returnOrderDetail.originalOrderNo }}
        </el-descriptions-item>
        <el-descriptions-item label="预期处理方式" v-if="returnOrderDetail.expectedHandleType !== undefined && returnOrderDetail.expectedHandleType !== null">
          {{ getHandleTypeText(returnOrderDetail.expectedHandleType) }}
        </el-descriptions-item>
        <el-descriptions-item label="状态" v-if="returnOrderDetail.status">
          <el-tag :type="getStatusTagType(returnOrderDetail.status)">
            {{ getReturnOrderStatusText(returnOrderDetail.status) }}
          </el-tag>
        </el-descriptions-item>
        <el-descriptions-item label="处理结果" v-if="returnOrderDetail.handleResult">
          {{ returnOrderDetail.handleResult }}
        </el-descriptions-item>
        <el-descriptions-item label="返货金额(元)" v-if="returnOrderDetail.returnAmount">
          <span class="amount">¥{{ formatMoney(returnOrderDetail.returnAmount) }}</span>
        </el-descriptions-item>
        <el-descriptions-item label="退款金额(元)" v-if="returnOrderDetail.refundAmount">
          <span class="amount">¥{{ formatMoney(returnOrderDetail.refundAmount) }}</span>
        </el-descriptions-item>
        <el-descriptions-item label="仓库ID" v-if="returnOrderDetail.warehouseId">
          {{ returnOrderDetail.warehouseId }}
        </el-descriptions-item>
        <el-descriptions-item label="仓库名称" v-if="returnOrderDetail.warehouseName">
          {{ returnOrderDetail.warehouseName }}
        </el-descriptions-item>
        <el-descriptions-item label="业务员ID" v-if="returnOrderDetail.salesmanId">
          {{ returnOrderDetail.salesmanId }}
        </el-descriptions-item>
        <el-descriptions-item label="客户ID" v-if="returnOrderDetail.customerId">
          {{ returnOrderDetail.customerId }}
        </el-descriptions-item>
        <el-descriptions-item label="创建人ID" v-if="returnOrderDetail.creatorId">
          {{ returnOrderDetail.creatorId }}
        </el-descriptions-item>
        <el-descriptions-item label="创建人姓名" v-if="returnOrderDetail.creatorName">
          {{ returnOrderDetail.creatorName }}
        </el-descriptions-item>
        <el-descriptions-item label="审核人ID" v-if="returnOrderDetail.auditorId">
          {{ returnOrderDetail.auditorId }}
        </el-descriptions-item>
        <el-descriptions-item label="审核时间" v-if="returnOrderDetail.auditTime">
          {{ formatDateTime(returnOrderDetail.auditTime) }}
        </el-descriptions-item>
        <el-descriptions-item label="处理人ID" v-if="returnOrderDetail.handlerId">
          {{ returnOrderDetail.handlerId }}
        </el-descriptions-item>
        <el-descriptions-item label="处理人姓名" v-if="returnOrderDetail.handlerName">
          {{ returnOrderDetail.handlerName }}
        </el-descriptions-item>
        <el-descriptions-item label="处理时间" v-if="returnOrderDetail.handleTime">
          {{ formatDateTime(returnOrderDetail.handleTime) }}
        </el-descriptions-item>
        <el-descriptions-item label="收货时间" v-if="returnOrderDetail.receiveTime">
          {{ formatDateTime(returnOrderDetail.receiveTime) }}
        </el-descriptions-item>
        <el-descriptions-item label="物流单号" v-if="returnOrderDetail.logisticsNo">
          {{ returnOrderDetail.logisticsNo }}
        </el-descriptions-item>
        <el-descriptions-item label="物流公司" v-if="returnOrderDetail.logisticsCompany">
          {{ returnOrderDetail.logisticsCompany }}
        </el-descriptions-item>
        <el-descriptions-item label="维修厂商" v-if="returnOrderDetail.repairVendor">
          {{ returnOrderDetail.repairVendor }}
        </el-descriptions-item>
        <el-descriptions-item label="审批流程ID" v-if="returnOrderDetail.approvalFlowId">
          {{ returnOrderDetail.approvalFlowId }}
        </el-descriptions-item>
        <el-descriptions-item label="备注" v-if="returnOrderDetail.remark" :span="2">
          {{ returnOrderDetail.remark }}
        </el-descriptions-item>
        <el-descriptions-item label="创建时间" v-if="returnOrderDetail.createTime">
          {{ formatDateTime(returnOrderDetail.createTime) }}
        </el-descriptions-item>
        <el-descriptions-item label="更新时间" v-if="returnOrderDetail.updateTime">
          {{ formatDateTime(returnOrderDetail.updateTime) }}
        </el-descriptions-item>
      </el-descriptions>
      
      <!-- 返货单附件（独立展示，避免在 el-descriptions 中导致 ResizeObserver 错误） -->
      <div v-if="returnOrderAttachments && returnOrderAttachments.length" class="return-order-attachments-section">
        <h4 class="attachments-section-title">附件</h4>
        <div class="attachment-list">
          <!-- 图片附件：显示缩略图 -->
          <div
            v-for="(attachment, index) in returnOrderAttachments"
            :key="index"
            :class="['attachment-item', { 'attachment-item-image': isAttachmentImage(attachment) }]"
          >
            <template v-if="isAttachmentImage(attachment)">
              <div class="attachment-image-wrapper">
                <el-image
                  :src="getAttachmentUrl(attachment)"
                  :alt="getAttachmentFileName(attachment)"
                  :preview-src-list="getImagePreviewList()"
                  :initial-index="getImageIndex(index)"
                  fit="cover"
                  class="attachment-image-preview"
                  :preview-teleported="true"
                  crossorigin="anonymous"
                  referrerpolicy="no-referrer-when-downgrade"
                  @error="handleImageError"
                >
                  <template #error>
                    <div class="image-slot">
                      <el-icon><Picture /></el-icon>
                    </div>
                  </template>
                </el-image>
                <!-- 下载按钮覆盖层 -->
                <div class="download-overlay">
                  <el-button
                    type="primary"
                    :icon="Download"
                    circle
                    size="small"
                    class="download-overlay-btn"
                    @click.stop="handleDownloadAttachment(attachment)"
                    title="下载图片"
                  />
                </div>
              </div>
              <div class="attachment-image-info">
                <div class="attachment-image-details">
                  <span class="attachment-name" :title="getAttachmentFileName(attachment)">
                    {{ getAttachmentFileName(attachment) }}
                  </span>
                  <span v-if="getAttachmentSize(attachment) > 0" class="attachment-size">
                    ({{ formatFileSize(getAttachmentSize(attachment)) }})
                  </span>
                </div>
              </div>
            </template>
            
            <!-- 非图片附件：显示图标 -->
            <template v-else>
              <div :class="['attachment-icon-wrapper', getFileIconClass(attachment)]">
                <el-icon class="attachment-icon">
                  <component :is="getFileIcon(attachment)" />
                </el-icon>
                <span v-if="getFileTypeLabel(attachment)" class="file-type-label">
                  {{ getFileTypeLabel(attachment) }}
                </span>
              </div>
              <span class="attachment-name" :title="getAttachmentFileName(attachment)">
                {{ getAttachmentFileName(attachment) }}
              </span>
              <span v-if="getAttachmentSize(attachment) > 0" class="attachment-size">
                ({{ formatFileSize(getAttachmentSize(attachment)) }})
              </span>
              <div class="attachment-actions">
                <el-button
                  type="primary"
                  size="small"
                  :icon="Download"
                  link
                  @click="handleDownloadAttachment(attachment)"
                >
                  下载
                </el-button>
              </div>
            </template>
          </div>
        </div>
      </div>
      <div v-else-if="returnOrderDetail.attachments" class="return-order-attachments-section">
        <h4 class="attachments-section-title">附件</h4>
        <span>无附件</span>
      </div>
      
      <!-- 返货明细表格（独立展示，避免在 el-descriptions 中导致 ResizeObserver 错误） -->
      <div v-if="returnOrderDetail.items && returnOrderDetail.items.length > 0" class="return-order-items-section">
        <h4 class="items-section-title">返货明细</h4>
        <el-table 
          :data="returnOrderDetail.items" 
          border 
          style="width: 100%" 
          size="small"
          :key="`items-${returnOrderDetail.items.length}`"
        >
          <el-table-column type="index" label="序号" width="60" align="center" />
          <el-table-column prop="productName" label="商品名称" min-width="150" show-overflow-tooltip />
          <el-table-column prop="productSpec" label="规格" min-width="120" show-overflow-tooltip />
          <el-table-column prop="quantity" label="数量" width="100" align="right">
            <template #default="{ row }">
              {{ row.quantity || 0 }}
            </template>
          </el-table-column>
          <el-table-column prop="unitPrice" label="单价(元)" width="120" align="right">
            <template #default="{ row }">
              {{ formatMoney(row.unitPrice || 0) }}
            </template>
          </el-table-column>
          <el-table-column prop="returnAmount" label="金额(元)" width="120" align="right">
            <template #default="{ row }">
              <span style="color: #f56c6c; font-weight: 600">
                {{ formatMoney(row.returnAmount || 0) }}
              </span>
            </template>
          </el-table-column>
          <el-table-column prop="reasonDetail" label="原因说明" min-width="150" show-overflow-tooltip />
        </el-table>
      </div>
    </div>

    <!-- 报销申请详情 -->
    <div v-else-if="businessType === 'TRAVEL_EXPENSE' && travelExpenseDetail" class="travel-expense-detail">
      <el-descriptions :column="2" border>
        <el-descriptions-item label="报销事由" :span="2">
          <span class="expense-title">{{ travelExpenseDetail.expenseReason || travelExpenseDetail.expenseTitle || '-' }}</span>
        </el-descriptions-item>
        <el-descriptions-item label="报销金额(元)">
          <span class="amount">¥{{ formatMoney(travelExpenseDetail.expenseAmount) }}</span>
        </el-descriptions-item>
        <!-- 报销类型：差旅报销 / 事项报销 -->
        <el-descriptions-item label="报销类型" v-if="travelExpenseDetail.expenseType">
          {{ getTravelExpenseTypeName(travelExpenseDetail.expenseType) }}
        </el-descriptions-item>
        <!-- 差旅报销：显示出行日期 + 结束日期 -->
        <el-descriptions-item
          label="出行日期"
          v-if="travelExpenseDetail.travelDate && getTravelExpenseTypeKey(travelExpenseDetail.expenseType) === 'TRAVEL'"
        >
          {{ formatDate(travelExpenseDetail.travelDate) }}
        </el-descriptions-item>
        <el-descriptions-item
          label="结束日期"
          v-if="travelExpenseDetail.endDate && getTravelExpenseTypeKey(travelExpenseDetail.expenseType) === 'TRAVEL'"
        >
          {{ formatDate(travelExpenseDetail.endDate) }}
        </el-descriptions-item>
        <!-- 事项报销：仅显示申报日期（作为发生日期） -->
        <el-descriptions-item
          label="申报日期"
          v-if="travelExpenseDetail.travelDate && getTravelExpenseTypeKey(travelExpenseDetail.expenseType) === 'ITEM'"
        >
          {{ formatDate(travelExpenseDetail.travelDate) }}
        </el-descriptions-item>
        <el-descriptions-item label="员工ID" v-if="travelExpenseDetail.employeeId">
          {{ travelExpenseDetail.employeeId }}
        </el-descriptions-item>
        <el-descriptions-item label="员工姓名" v-if="travelExpenseDetail.employeeName">
          {{ travelExpenseDetail.employeeName }}
        </el-descriptions-item>
        <el-descriptions-item label="业务员ID" v-if="travelExpenseDetail.salesmanId">
          {{ travelExpenseDetail.salesmanId }}
        </el-descriptions-item>
        <el-descriptions-item label="状态" v-if="travelExpenseDetail.status">
          <el-tag :type="getStatusTagType(travelExpenseDetail.status)">
            {{ getTravelExpenseStatusText(travelExpenseDetail.status) }}
          </el-tag>
        </el-descriptions-item>
        <el-descriptions-item label="提交时间" v-if="travelExpenseDetail.submitTime">
          {{ formatDateTime(travelExpenseDetail.submitTime) }}
        </el-descriptions-item>
        <el-descriptions-item label="审批时间" v-if="travelExpenseDetail.approvalTime">
          {{ formatDateTime(travelExpenseDetail.approvalTime) }}
        </el-descriptions-item>
        <el-descriptions-item label="创建人" v-if="travelExpenseDetail.createBy">
          {{ travelExpenseDetail.createBy }}
        </el-descriptions-item>
        <el-descriptions-item label="创建时间" v-if="travelExpenseDetail.createTime">
          {{ formatDateTime(travelExpenseDetail.createTime) }}
        </el-descriptions-item>
        <el-descriptions-item label="更新人" v-if="travelExpenseDetail.updateBy">
          {{ travelExpenseDetail.updateBy }}
        </el-descriptions-item>
        <el-descriptions-item label="更新时间" v-if="travelExpenseDetail.updateTime">
          {{ formatDateTime(travelExpenseDetail.updateTime) }}
        </el-descriptions-item>
      </el-descriptions>
      
      <!-- 报销申请附件（独立展示，避免在 el-descriptions 中导致 ResizeObserver 错误） -->
      <div v-if="travelExpenseAttachments && travelExpenseAttachments.length" class="travel-expense-attachments-section">
        <h4 class="attachments-section-title">附件</h4>
        <div class="attachment-list">
          <!-- 图片附件：显示缩略图 -->
          <div
            v-for="(attachment, index) in travelExpenseAttachments"
            :key="index"
            :class="['attachment-item', { 'attachment-item-image': isAttachmentImage(attachment) }]"
          >
            <template v-if="isAttachmentImage(attachment)">
              <div class="attachment-image-wrapper">
                <el-image
                  :src="getAttachmentUrl(attachment)"
                  :alt="getAttachmentFileName(attachment)"
                  :preview-src-list="getTravelExpenseImagePreviewList()"
                  :initial-index="getTravelExpenseImageIndex(index)"
                  fit="cover"
                  class="attachment-image-preview"
                  :preview-teleported="true"
                  crossorigin="anonymous"
                  referrerpolicy="no-referrer-when-downgrade"
                  @error="handleImageError"
                >
                  <template #error>
                    <div class="image-slot">
                      <el-icon><Picture /></el-icon>
                    </div>
                  </template>
                </el-image>
                <!-- 下载按钮覆盖层 -->
                <div class="download-overlay">
                  <el-button
                    type="primary"
                    :icon="Download"
                    circle
                    size="small"
                    class="download-overlay-btn"
                    @click.stop="handleDownloadAttachment(attachment)"
                    title="下载图片"
                  />
                </div>
              </div>
              <div class="attachment-image-info">
                <div class="attachment-image-details">
                  <span class="attachment-name" :title="getAttachmentFileName(attachment)">
                    {{ getAttachmentFileName(attachment) }}
                  </span>
                  <span v-if="getAttachmentSize(attachment) > 0" class="attachment-size">
                    ({{ formatFileSize(getAttachmentSize(attachment)) }})
                  </span>
                </div>
              </div>
            </template>
            
            <!-- 非图片附件：显示图标 -->
            <template v-else>
              <div :class="['attachment-icon-wrapper', getFileIconClass(attachment)]">
                <el-icon class="attachment-icon">
                  <component :is="getFileIcon(attachment)" />
                </el-icon>
                <span v-if="getFileTypeLabel(attachment)" class="file-type-label">
                  {{ getFileTypeLabel(attachment) }}
                </span>
              </div>
              <span class="attachment-name" :title="getAttachmentFileName(attachment)">
                {{ getAttachmentFileName(attachment) }}
              </span>
              <span v-if="getAttachmentSize(attachment) > 0" class="attachment-size">
                ({{ formatFileSize(getAttachmentSize(attachment)) }})
              </span>
              <div class="attachment-actions">
                <el-button
                  type="primary"
                  size="small"
                  :icon="Download"
                  link
                  @click="handleDownloadAttachment(attachment)"
                >
                  下载
                </el-button>
              </div>
            </template>
          </div>
        </div>
      </div>
      <div v-else-if="travelExpenseDetail.attachments || travelExpenseDetail.attachmentUrls" class="travel-expense-attachments-section">
        <h4 class="attachments-section-title">附件</h4>
        <span>无附件</span>
      </div>
    </div>

    <!-- 出差申请详情 -->
    <div v-else-if="businessType === 'BUSINESS_TRIP' && businessTripDetail" class="business-trip-detail">
      <el-descriptions :column="2" border>
        <el-descriptions-item label="出差事由" :span="2">
          <span class="trip-title">{{ businessTripDetail.reason || businessTripDetail.tripTitle || '-' }}</span>
        </el-descriptions-item>
        <el-descriptions-item label="出差地点">
          {{ businessTripDetail.tripDestination || businessTripDetail.destination || '-' }}
        </el-descriptions-item>
        <el-descriptions-item label="出差状态" v-if="businessTripDetail.status">
          <el-tag :type="getStatusTagType(businessTripDetail.status)">
            {{ getBusinessTripStatusText(businessTripDetail.status) }}
          </el-tag>
        </el-descriptions-item>
        <el-descriptions-item label="开始时间" v-if="businessTripDetail.tripStartTime">
          {{ formatDateTime(businessTripDetail.tripStartTime) }}
        </el-descriptions-item>
        <el-descriptions-item label="结束时间" v-if="businessTripDetail.tripEndTime">
          {{ formatDateTime(businessTripDetail.tripEndTime) }}
        </el-descriptions-item>
        <el-descriptions-item label="出差日期" v-if="businessTripDetail.tripDate && !businessTripDetail.tripStartTime">
          {{ formatDate(businessTripDetail.tripDate) }}
        </el-descriptions-item>
        <el-descriptions-item label="员工ID" v-if="businessTripDetail.employeeId">
          {{ businessTripDetail.employeeId }}
        </el-descriptions-item>
        <el-descriptions-item label="业务员ID" v-if="businessTripDetail.salesmanId">
          {{ businessTripDetail.salesmanId }}
        </el-descriptions-item>
        <el-descriptions-item label="提交时间" v-if="businessTripDetail.submitTime">
          {{ formatDateTime(businessTripDetail.submitTime) }}
        </el-descriptions-item>
        <el-descriptions-item label="审批时间" v-if="businessTripDetail.approvalTime">
          {{ formatDateTime(businessTripDetail.approvalTime) }}
        </el-descriptions-item>
        <el-descriptions-item label="创建人" v-if="businessTripDetail.createBy">
          {{ businessTripDetail.createBy }}
        </el-descriptions-item>
        <el-descriptions-item label="创建时间" v-if="businessTripDetail.createTime">
          {{ formatDateTime(businessTripDetail.createTime) }}
        </el-descriptions-item>
        <el-descriptions-item label="更新人" v-if="businessTripDetail.updateBy">
          {{ businessTripDetail.updateBy }}
        </el-descriptions-item>
        <el-descriptions-item label="更新时间" v-if="businessTripDetail.updateTime">
          {{ formatDateTime(businessTripDetail.updateTime) }}
        </el-descriptions-item>
      </el-descriptions>
      
      <!-- 出差申请附件（独立展示，避免在 el-descriptions 中导致 ResizeObserver 错误） -->
      <div v-if="businessTripAttachments && businessTripAttachments.length" class="business-trip-attachments-section">
        <h4 class="attachments-section-title">附件</h4>
        <div class="attachment-list">
          <!-- 图片附件：显示缩略图 -->
          <div
            v-for="(attachment, index) in businessTripAttachments"
            :key="index"
            :class="['attachment-item', { 'attachment-item-image': isAttachmentImage(attachment) }]"
          >
            <template v-if="isAttachmentImage(attachment)">
              <div class="attachment-image-wrapper">
                <el-image
                  :src="getAttachmentUrl(attachment)"
                  :alt="getAttachmentFileName(attachment)"
                  :preview-src-list="getBusinessTripImagePreviewList()"
                  :initial-index="getBusinessTripImageIndex(index)"
                  fit="cover"
                  class="attachment-image-preview"
                  :preview-teleported="true"
                  crossorigin="anonymous"
                  referrerpolicy="no-referrer-when-downgrade"
                  @error="handleImageError"
                >
                  <template #error>
                    <div class="image-slot">
                      <el-icon><Picture /></el-icon>
                    </div>
                  </template>
                </el-image>
                <!-- 下载按钮覆盖层 -->
                <div class="download-overlay">
                  <el-button
                    type="primary"
                    :icon="Download"
                    circle
                    size="small"
                    class="download-overlay-btn"
                    @click.stop="handleDownloadAttachment(attachment)"
                    title="下载图片"
                  />
                </div>
              </div>
              <div class="attachment-image-info">
                <div class="attachment-image-details">
                  <span class="attachment-name" :title="getAttachmentFileName(attachment)">
                    {{ getAttachmentFileName(attachment) }}
                  </span>
                  <span v-if="getAttachmentSize(attachment) > 0" class="attachment-size">
                    ({{ formatFileSize(getAttachmentSize(attachment)) }})
                  </span>
                </div>
              </div>
            </template>
            
            <!-- 非图片附件：显示图标 -->
            <template v-else>
              <div :class="['attachment-icon-wrapper', getFileIconClass(attachment)]">
                <el-icon class="attachment-icon">
                  <component :is="getFileIcon(attachment)" />
                </el-icon>
                <span v-if="getFileTypeLabel(attachment)" class="file-type-label">
                  {{ getFileTypeLabel(attachment) }}
                </span>
              </div>
              <span class="attachment-name" :title="getAttachmentFileName(attachment)">
                {{ getAttachmentFileName(attachment) }}
              </span>
              <span v-if="getAttachmentSize(attachment) > 0" class="attachment-size">
                ({{ formatFileSize(getAttachmentSize(attachment)) }})
              </span>
              <div class="attachment-actions">
                <el-button
                  type="primary"
                  size="small"
                  :icon="Download"
                  link
                  @click="handleDownloadAttachment(attachment)"
                >
                  下载
                </el-button>
              </div>
            </template>
          </div>
        </div>
      </div>
      <div v-else-if="businessTripDetail.attachments || businessTripDetail.attachmentUrls" class="business-trip-attachments-section">
        <h4 class="attachments-section-title">附件</h4>
        <span>无附件</span>
      </div>
    </div>

    <!-- 未知业务类型或暂无数据 -->
    <div v-else class="no-detail">
      <el-empty description="暂无业务详情数据" />
    </div>
  </div>
</template>

<script setup>
import { ref, watch, onMounted, computed } from 'vue'
import { ElMessage } from 'element-plus'
import { getBusinessDetail } from '@/api/managerApproval'
import { getReturnOrderDetail } from '@/api/salesman'
import { getFileName } from '@/api/file'
import { formatFileSize, isImageFile, downloadFileFromServer } from '@/utils/fileUtils'
import { getPreviewUrl } from '../api/file'
import { Download, Picture, Document, VideoPlay } from '@element-plus/icons-vue'
import ImagePreview from './ImagePreview.vue'
import AttachmentUpload from './AttachmentUpload.vue'

// eslint-disable-next-line no-undef
const props = defineProps({
  businessType: {
    type: String,
    required: true
  },
  businessId: {
    type: [String, Number],
    required: true
  }
})

const loading = ref(false)
const dealDetail = ref(null)
const dealAttachments = ref([])
const contractDetail = ref(null)
// 合同附件数据（统一格式转换）
const contractAttachments = computed(() => {
  if (!contractDetail.value) {
    return []
  }
  
  const data = contractDetail.value
  let attachments = []
  
  // 优先使用 contractAttachmentInfos（包含文件大小信息）
  if (data.contractAttachmentInfos && Array.isArray(data.contractAttachmentInfos) && data.contractAttachmentInfos.length > 0) {
    attachments = data.contractAttachmentInfos.map(att => {
      // 确保URL格式正确
      let url = att.url || att.fileUrl || att.file_url || ''
      return {
        url: url,
        fileName: att.fileName || att.originalFileName || '附件',
        originalFileName: att.originalFileName || att.fileName || '附件',
        size: att.size || 0
      }
    })
  } else if (data.contractAttachments) {
    // 兼容旧格式：如果 contractAttachmentInfos 不存在，使用 contractAttachments
    if (Array.isArray(data.contractAttachments)) {
      attachments = data.contractAttachments.map(att => {
        // 如果是字符串（URL），转换为对象格式
        if (typeof att === 'string') {
          return {
            url: att,
            fileName: att.split('/').pop() || '附件',
            originalFileName: att.split('/').pop() || '附件',
            size: 0
          }
        }
        // 如果已经是对象，确保格式正确
        return {
          url: att.url || att.fileUrl || att.file_url || '',
          fileName: att.fileName || att.originalFileName || att.name || '附件',
          originalFileName: att.originalFileName || att.fileName || att.name || '附件',
          size: att.size || 0
        }
      })
    } else if (typeof data.contractAttachments === 'string') {
      // 如果是字符串，尝试解析
      try {
        const parsed = JSON.parse(data.contractAttachments)
        const urls = Array.isArray(parsed) ? parsed : [parsed]
        attachments = urls.map(url => {
          const urlStr = typeof url === 'string' ? url : (url.url || url.fileUrl || '')
          return {
            url: urlStr,
            fileName: urlStr.split('/').pop() || '附件',
            originalFileName: urlStr.split('/').pop() || '附件',
            size: 0
          }
        })
      } catch {
        // 如果解析失败，按逗号分割
        const urlArray = data.contractAttachments.split(',').map(s => s.trim()).filter(Boolean)
        attachments = urlArray.map(url => ({
          url: url,
          fileName: url.split('/').pop() || '附件',
          originalFileName: url.split('/').pop() || '附件',
          size: 0
        }))
      }
    }
  }
  
  // 确保所有附件URL都通过代理（如果是COS URL）
  attachments = attachments.map(att => {
    if (att.url && (att.url.includes('cos.') || att.url.includes('myqcloud.com')) && !att.url.includes('/files/preview')) {
      console.log('[BusinessDetail] 合同附件URL需要转换:', att.url)
      // 注意：这里不直接转换，让AttachmentUpload组件处理
      // 但确保URL格式正确
    }
    return att
  })
  
  console.log('[BusinessDetail] 合同附件数据转换完成:', attachments)
  return attachments
})

const returnOrderDetail = ref(null)
const returnOrderAttachments = ref([])
const travelExpenseDetail = ref(null)
const travelExpenseAttachments = ref([])
const businessTripDetail = ref(null)
const businessTripAttachments = ref([])

// 获取企业ID（用于判断是否是个人用户）
const getCompanyId = () => {
  try {
    const userInfoStr = localStorage.getItem('userInfo')
    if (userInfoStr) {
      const userInfo = JSON.parse(userInfoStr)
      return userInfo.companyId || null
    }
  } catch (e) {
    console.warn('解析用户信息失败', e)
  }
  return null
}

// 加载业务详情
const loadBusinessDetail = async () => {
  if (!props.businessType || !props.businessId) {
    return
  }

  loading.value = true
  try {
    let res = null
    
    // 个人用户（没有企业ID）且是返货单类型，使用业务员端接口
    const companyId = getCompanyId()
    if (!companyId && props.businessType === 'RETURN_ORDER') {
      try {
        res = await getReturnOrderDetail(Number(props.businessId))
      } catch (error) {
        console.error('使用业务员端接口加载返货单详情失败:', error)
        // 如果业务员端接口失败，尝试使用管理员端接口
        res = await getBusinessDetail(props.businessType, props.businessId)
      }
    } else {
      // 其他情况使用管理员端接口
      res = await getBusinessDetail(props.businessType, props.businessId)
    }
    
    if (res.code === '000000' && res.data) {
      // 根据业务类型设置对应的详情数据
      switch (props.businessType) {
        case 'CUSTOMER_DEAL_RECORD':
          dealDetail.value = res.data
          // 处理成交台账附件数据
          await processDealAttachments(res.data)
          break
        case 'CONTRACT':
          contractDetail.value = res.data
          break
        case 'RETURN_ORDER':
          returnOrderDetail.value = res.data
          // 处理返货单附件数据
          await processReturnOrderAttachments(res.data)
          // 后端已返回包含返货明细的完整信息，无需降级调用
          break
        case 'TRAVEL_EXPENSE':
          travelExpenseDetail.value = res.data
          // 处理报销申请附件数据
          await processTravelExpenseAttachments(res.data)
          break
        case 'BUSINESS_TRIP':
          businessTripDetail.value = res.data
          // 处理出差申请附件数据
          await processBusinessTripAttachments(res.data)
          break
        default:
          console.warn('未知的业务类型:', props.businessType)
      }
    }
  } catch (error) {
    console.error('加载业务详情失败:', error)
    ElMessage.error('加载业务详情失败')
  } finally {
    loading.value = false
  }
}

// 格式化金额
const formatMoney = (amount) => {
  if (amount === null || amount === undefined) return '0.00'
  return Number(amount).toFixed(2)
}

// 格式化日期
const formatDate = (dateStr) => {
  if (!dateStr) return '-'
  const date = new Date(dateStr)
  if (isNaN(date.getTime())) return dateStr
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

// 格式化日期时间
const formatDateTime = (dateStr) => {
  if (!dateStr) return '-'
  const date = new Date(dateStr)
  if (isNaN(date.getTime())) return dateStr
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')
  const seconds = String(date.getSeconds()).padStart(2, '0')
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`
}

// 获取成交类型文本
const getDealTypeText = (dealType) => {
  if (!dealType) return '-'
  const typeLower = String(dealType).toLowerCase().trim()
  const typeMap = {
    'new_customer': '新客户',
    'renewal': '续约',
    'upsell': '增购',
    'cross_sell': '交叉销售'
  }
  return typeMap[typeLower] || dealType
}

// 获取付款状态文本
const getPaymentStatusText = (paymentStatus) => {
  if (!paymentStatus) return '-'
  const statusLower = String(paymentStatus).toLowerCase().trim()
  const statusMap = {
    'unpaid': '未付款',
    'partial': '部分付款',
    'paid': '已付款'
  }
  return statusMap[statusLower] || paymentStatus
}

// 获取付款状态标签类型
const getPaymentStatusTagType = (paymentStatus) => {
  const typeMap = {
    'unpaid': 'danger',
    'partial': 'warning',
    'paid': 'success'
  }
  return typeMap[paymentStatus] || 'info'
}

// 获取合同状态文本
const getContractStatusText = (status) => {
  if (!status) return '-'
  const statusUpper = String(status).toUpperCase().trim()
  const statusMap = {
    'DRAFT': '草稿',
    'PENDING': '待审核',
    'APPROVING': '审批中',
    'APPROVED': '已审核',
    'REJECTED': '已驳回',
    'SIGNED': '已签订',
    'EXECUTING': '执行中',
    'COMPLETED': '已完成',
    'TERMINATED': '已终止'
  }
  return statusMap[statusUpper] || status
}

// 获取合同类型文本
const getContractTypeText = (type) => {
  if (!type) return '-'
  const typeLower = String(type).toLowerCase().trim()
  const typeMap = {
    'intention': '意向合同',
    'formal': '正式合同',
    'framework': '框架合同',
    'supplement': '补充合同'
  }
  return typeMap[typeLower] || type
}

// 获取合同状态标签类型
const getContractStatusTagType = (status) => {
  if (!status) return 'info'
  const statusUpper = String(status).toUpperCase().trim()
  const typeMap = {
    'DRAFT': 'info',
    'PENDING': 'warning',
    'APPROVING': 'primary',
    'APPROVED': 'success',
    'REJECTED': 'danger',
    'SIGNED': 'success',
    'EXECUTING': 'primary',
    'COMPLETED': 'success',
    'TERMINATED': 'info'
  }
  return typeMap[statusUpper] || 'info'
}

// 获取状态标签类型（通用）
const getStatusTagType = (status) => {
  const typeMap = {
    'PENDING': 'warning',
    'APPROVING': 'primary',
    'APPROVED': 'success',
    'REJECTED': 'danger',
    'COMPLETED': 'success',
    'CANCELLED': 'info',
    'DRAFT': 'info'
  }
  return typeMap[status] || 'info'
}

// 获取返货类型文本
const getReturnTypeText = (type) => {
  const typeMap = {
    1: '质量问题',
    2: '规格不符',
    3: '其他原因'
  }
  return typeMap[type] || `类型${type}`
}

// 获取处理方式文本
const getHandleTypeText = (type) => {
  const typeMap = {
    1: '退货',
    2: '换货',
    3: '维修',
    4: '其他'
  }
  return typeMap[type] || `方式${type}`
}

// 获取出差状态文本
// 出差申请状态值：draft(草稿), pending(待审批), approving(审批中), approved(已通过), rejected(已驳回), cancelled(已取消)
const getBusinessTripStatusText = (status) => {
  if (!status) return '-'
  const statusUpper = String(status).toUpperCase().trim()
  const statusMap = {
    'DRAFT': '草稿',
    'PENDING': '待审批',
    'APPROVING': '审批中',
    'APPROVED': '已通过',
    'REJECTED': '已驳回',
    'CANCELLED': '已取消',
    'CANCELED': '已取消' // 兼容美式拼写
  }
  return statusMap[statusUpper] || status
}

// 报销类型：TRAVEL-差旅报销，ITEM-事项报销
const getTravelExpenseTypeKey = (type) => {
  if (!type) return 'TRAVEL'
  return String(type).toUpperCase().trim()
}

const getTravelExpenseTypeName = (type) => {
  const key = getTravelExpenseTypeKey(type)
  if (key === 'ITEM') return '事项报销'
  return '差旅报销'
}

// 获取报销申请状态文本
const getTravelExpenseStatusText = (status) => {
  if (!status) return '-'
  const statusUpper = status.toUpperCase()
  const statusMap = {
    'DRAFT': '草稿',
    'PENDING': '待审批',
    'APPROVING': '审批中',
    'APPROVED': '已通过',
    'REJECTED': '已驳回',
    'CANCELLED': '已取消'
  }
  return statusMap[statusUpper] || status
}

// 获取返货单状态文本
const getReturnOrderStatusText = (status) => {
  if (!status) return '-'
  const statusUpper = status.toUpperCase()
  const statusMap = {
    'DRAFT': '草稿',
    'PENDING': '待审批',
    'APPROVING': '审批中',
    'APPROVED': '已通过',
    'REJECTED': '已驳回',
    'CANCELLED': '已取消'
  }
  return statusMap[statusUpper] || status
}

// 获取成交台账状态文本
const getDealStatusText = (status) => {
  if (!status) return '-'
  const statusLower = status.toLowerCase()
  const statusMap = {
    'draft': '草稿',
    'pending': '待审批',
    'approving': '审批中',
    'approved': '已通过',
    'rejected': '已驳回',
    'cancelled': '已取消'
  }
  return statusMap[statusLower] || status
}

// 处理成交台账附件数据
const processDealAttachments = async (data) => {
  dealAttachments.value = []
  if (!data || !data.attachments) return
  
  const baseURL = process.env.VUE_APP_BASE_API || 'http://localhost:8086'
  let attachments = []
  
  // 优先使用 attachmentInfos（包含文件大小信息）
  if (data.attachmentInfos && Array.isArray(data.attachmentInfos) && data.attachmentInfos.length > 0) {
    attachments = data.attachmentInfos.map(att => {
      let url = att.fileUrl || att.url || att
      if (typeof url === 'string' && !url.startsWith('http://') && !url.startsWith('https://')) {
        url = url.startsWith('/') ? baseURL + url : `${baseURL}/${url}`
      }
      return {
        url,
        fileName: att.fileName || url.split('/').pop() || '附件',
        originalFileName: att.originalFileName || att.fileName || url.split('/').pop() || '附件',
        size: att.size || 0
      }
    })
  } else if (data.attachments) {
    if (Array.isArray(data.attachments)) {
      attachments = await Promise.all(data.attachments.map(async (att) => {
        if (typeof att === 'string') {
          let url = att
          if (!url.startsWith('http://') && !url.startsWith('https://')) {
            url = url.startsWith('/') ? baseURL + url : `${baseURL}/${url}`
          }
          // 尝试从后端获取原始文件名和文件大小
          let originalFileName = url.split('/').pop() || '附件'
          let fileSize = 0
          try {
            const fileNameRes = await getFileName(url)
            if (fileNameRes && fileNameRes.code === '000000') {
              originalFileName = fileNameRes.data || fileNameRes.msg || originalFileName
            }
          } catch (error) {
            console.debug('获取文件名失败:', error)
          }
          // 尝试通过 HEAD 请求获取文件大小
          try {
            const response = await fetch(url, { method: 'HEAD' })
            if (response.ok) {
              const contentLength = response.headers.get('Content-Length')
              if (contentLength) {
                fileSize = parseInt(contentLength, 10) || 0
              }
            }
          } catch (error) {
            console.debug('获取文件大小失败:', error)
          }
          return { 
            url, 
            fileName: originalFileName, // 使用原始文件名
            originalFileName, // 保存原始文件名
            size: fileSize 
          }
        }
        return att
      }))
    } else if (typeof data.attachments === 'string') {
      try {
        const parsed = JSON.parse(data.attachments)
        const urls = Array.isArray(parsed) ? parsed : [parsed]
        attachments = await Promise.all(urls.map(async (url) => {
          if (typeof url === 'string') {
            let fullUrl = url
            if (!fullUrl.startsWith('http://') && !fullUrl.startsWith('https://')) {
              fullUrl = fullUrl.startsWith('/') ? baseURL + fullUrl : `${baseURL}/${fullUrl}`
            }
            // 尝试从后端获取原始文件名和文件大小
            let originalFileName = fullUrl.split('/').pop() || '附件'
            let fileSize = 0
            try {
              const fileNameRes = await getFileName(fullUrl)
              if (fileNameRes && fileNameRes.code === '000000') {
                originalFileName = fileNameRes.data || fileNameRes.msg || originalFileName
              }
            } catch (error) {
              console.debug('获取文件名失败:', error)
            }
            // 尝试通过 HEAD 请求获取文件大小
            try {
              const response = await fetch(fullUrl, { method: 'HEAD' })
              if (response.ok) {
                const contentLength = response.headers.get('Content-Length')
                if (contentLength) {
                  fileSize = parseInt(contentLength, 10) || 0
                }
              }
            } catch (error) {
              console.debug('获取文件大小失败:', error)
            }
            return { 
              url: fullUrl, 
              fileName: originalFileName, // 使用原始文件名
              originalFileName, // 保存原始文件名
              size: fileSize 
            }
          }
          return url
        }))
      } catch {
        const urlArray = data.attachments.split(',').map(s => s.trim()).filter(Boolean)
        attachments = await Promise.all(urlArray.map(async (url) => {
          let fullUrl = url
          if (!fullUrl.startsWith('http://') && !fullUrl.startsWith('https://')) {
            fullUrl = fullUrl.startsWith('/') ? baseURL + fullUrl : `${baseURL}/${fullUrl}`
          }
          // 尝试从后端获取原始文件名
          let originalFileName = fullUrl.split('/').pop() || '附件'
          try {
            const fileNameRes = await getFileName(fullUrl)
            if (fileNameRes && fileNameRes.code === '000000') {
              originalFileName = fileNameRes.data || fileNameRes.msg || originalFileName
            }
          } catch (error) {
            console.debug('获取文件名失败:', error)
          }
          return { 
            url: fullUrl, 
            fileName: originalFileName, // 使用原始文件名
            originalFileName, // 保存原始文件名
            size: 0 
          }
        }))
      }
    }
  }
  
  dealAttachments.value = attachments
}

// 处理报销申请附件数据
const processTravelExpenseAttachments = async (data) => {
  travelExpenseAttachments.value = []
  if (!data) return
  
  // 优先使用 attachments 字段，如果没有则尝试 attachmentUrls
  let attachmentData = data.attachments || data.attachmentUrls || null
  if (!attachmentData) return
  
  const baseURL = process.env.VUE_APP_BASE_API || 'http://localhost:8086'
  let attachments = []
  
  // 优先使用 attachmentInfos（包含文件大小信息）
  if (data.attachmentInfos && Array.isArray(data.attachmentInfos) && data.attachmentInfos.length > 0) {
    attachments = data.attachmentInfos.map(att => {
      let url = att.fileUrl || att.url || att
      if (typeof url === 'string' && !url.startsWith('http://') && !url.startsWith('https://')) {
        url = url.startsWith('/') ? baseURL + url : `${baseURL}/${url}`
      }
      return {
        url,
        fileName: att.fileName || url.split('/').pop() || '附件',
        originalFileName: att.originalFileName || att.fileName || url.split('/').pop() || '附件',
        size: att.size || 0
      }
    })
  } else if (attachmentData) {
    if (Array.isArray(attachmentData)) {
      attachments = await Promise.all(attachmentData.map(async (att) => {
        if (typeof att === 'string') {
          let url = att
          if (!url.startsWith('http://') && !url.startsWith('https://')) {
            url = url.startsWith('/') ? baseURL + url : `${baseURL}/${url}`
          }
          // 尝试从后端获取原始文件名和文件大小
          let originalFileName = url.split('/').pop() || '附件'
          let fileSize = 0
          try {
            const fileNameRes = await getFileName(url)
            if (fileNameRes && fileNameRes.code === '000000') {
              originalFileName = fileNameRes.data || fileNameRes.msg || originalFileName
            }
          } catch (error) {
            console.debug('获取文件名失败:', error)
          }
          // 尝试通过 HEAD 请求获取文件大小
          try {
            const response = await fetch(url, { method: 'HEAD' })
            if (response.ok) {
              const contentLength = response.headers.get('Content-Length')
              if (contentLength) {
                fileSize = parseInt(contentLength, 10) || 0
              }
            }
          } catch (error) {
            console.debug('获取文件大小失败:', error)
          }
          return { 
            url, 
            fileName: originalFileName,
            originalFileName,
            size: fileSize 
          }
        }
        return att
      }))
    } else if (typeof attachmentData === 'string') {
      try {
        const parsed = JSON.parse(attachmentData)
        const urls = Array.isArray(parsed) ? parsed : [parsed]
        attachments = await Promise.all(urls.map(async (url) => {
          if (typeof url === 'string') {
            let fullUrl = url
            if (!fullUrl.startsWith('http://') && !fullUrl.startsWith('https://')) {
              fullUrl = fullUrl.startsWith('/') ? baseURL + fullUrl : `${baseURL}/${fullUrl}`
            }
            // 尝试从后端获取原始文件名和文件大小
            let originalFileName = fullUrl.split('/').pop() || '附件'
            let fileSize = 0
            try {
              const fileNameRes = await getFileName(fullUrl)
              if (fileNameRes && fileNameRes.code === '000000') {
                originalFileName = fileNameRes.data || fileNameRes.msg || originalFileName
              }
            } catch (error) {
              console.debug('获取文件名失败:', error)
            }
            // 尝试通过 HEAD 请求获取文件大小
            try {
              const response = await fetch(fullUrl, { method: 'HEAD' })
              if (response.ok) {
                const contentLength = response.headers.get('Content-Length')
                if (contentLength) {
                  fileSize = parseInt(contentLength, 10) || 0
                }
              }
            } catch (error) {
              console.debug('获取文件大小失败:', error)
            }
            return { 
              url: fullUrl, 
              fileName: originalFileName,
              originalFileName,
              size: fileSize 
            }
          }
          return url
        }))
      } catch {
        const urlArray = attachmentData.split(',').map(s => s.trim()).filter(Boolean)
        attachments = await Promise.all(urlArray.map(async (url) => {
          let fullUrl = url
          if (!fullUrl.startsWith('http://') && !fullUrl.startsWith('https://')) {
            fullUrl = fullUrl.startsWith('/') ? baseURL + fullUrl : `${baseURL}/${fullUrl}`
          }
          // 尝试从后端获取原始文件名
          let originalFileName = fullUrl.split('/').pop() || '附件'
          try {
            const fileNameRes = await getFileName(fullUrl)
            if (fileNameRes && fileNameRes.code === '000000') {
              originalFileName = fileNameRes.data || fileNameRes.msg || originalFileName
            }
          } catch (error) {
            console.debug('获取文件名失败:', error)
          }
          return { 
            url: fullUrl, 
            fileName: originalFileName,
            originalFileName,
            size: 0 
          }
        }))
      }
    }
  }
  
  travelExpenseAttachments.value = attachments
}

// 处理出差申请附件数据
const processBusinessTripAttachments = async (data) => {
  businessTripAttachments.value = []
  if (!data) return
  
  // 优先使用 attachments 字段，如果没有则尝试 attachmentUrls
  let attachmentData = data.attachments || data.attachmentUrls || null
  if (!attachmentData) return
  
  const baseURL = process.env.VUE_APP_BASE_API || 'http://localhost:8086'
  let attachments = []
  
  // 优先使用 attachmentInfos（包含文件大小信息）
  if (data.attachmentInfos && Array.isArray(data.attachmentInfos) && data.attachmentInfos.length > 0) {
    attachments = data.attachmentInfos.map(att => {
      let url = att.fileUrl || att.url || att
      if (typeof url === 'string' && !url.startsWith('http://') && !url.startsWith('https://')) {
        url = url.startsWith('/') ? baseURL + url : `${baseURL}/${url}`
      }
      return {
        url,
        fileName: att.fileName || url.split('/').pop() || '附件',
        originalFileName: att.originalFileName || att.fileName || url.split('/').pop() || '附件',
        size: att.size || 0
      }
    })
  } else if (attachmentData) {
    if (Array.isArray(attachmentData)) {
      attachments = await Promise.all(attachmentData.map(async (att) => {
        if (typeof att === 'string') {
          let url = att
          if (!url.startsWith('http://') && !url.startsWith('https://')) {
            url = url.startsWith('/') ? baseURL + url : `${baseURL}/${url}`
          }
          // 尝试从后端获取原始文件名和文件大小
          let originalFileName = url.split('/').pop() || '附件'
          let fileSize = 0
          try {
            const fileNameRes = await getFileName(url)
            if (fileNameRes && fileNameRes.code === '000000') {
              originalFileName = fileNameRes.data || fileNameRes.msg || originalFileName
            }
          } catch (error) {
            console.debug('获取文件名失败:', error)
          }
          // 尝试通过 HEAD 请求获取文件大小
          try {
            const response = await fetch(url, { method: 'HEAD' })
            if (response.ok) {
              const contentLength = response.headers.get('Content-Length')
              if (contentLength) {
                fileSize = parseInt(contentLength, 10) || 0
              }
            }
          } catch (error) {
            console.debug('获取文件大小失败:', error)
          }
          return { 
            url, 
            fileName: originalFileName,
            originalFileName,
            size: fileSize 
          }
        }
        return att
      }))
    } else if (typeof attachmentData === 'string') {
      try {
        const parsed = JSON.parse(attachmentData)
        const urls = Array.isArray(parsed) ? parsed : [parsed]
        attachments = await Promise.all(urls.map(async (url) => {
          if (typeof url === 'string') {
            let fullUrl = url
            if (!fullUrl.startsWith('http://') && !fullUrl.startsWith('https://')) {
              fullUrl = fullUrl.startsWith('/') ? baseURL + fullUrl : `${baseURL}/${fullUrl}`
            }
            // 尝试从后端获取原始文件名和文件大小
            let originalFileName = fullUrl.split('/').pop() || '附件'
            let fileSize = 0
            try {
              const fileNameRes = await getFileName(fullUrl)
              if (fileNameRes && fileNameRes.code === '000000') {
                originalFileName = fileNameRes.data || fileNameRes.msg || originalFileName
              }
            } catch (error) {
              console.debug('获取文件名失败:', error)
            }
            // 尝试通过 HEAD 请求获取文件大小
            try {
              const response = await fetch(fullUrl, { method: 'HEAD' })
              if (response.ok) {
                const contentLength = response.headers.get('Content-Length')
                if (contentLength) {
                  fileSize = parseInt(contentLength, 10) || 0
                }
              }
            } catch (error) {
              console.debug('获取文件大小失败:', error)
            }
            return { 
              url: fullUrl, 
              fileName: originalFileName,
              originalFileName,
              size: fileSize 
            }
          }
          return url
        }))
      } catch {
        const urlArray = attachmentData.split(',').map(s => s.trim()).filter(Boolean)
        attachments = await Promise.all(urlArray.map(async (url) => {
          let fullUrl = url
          if (!fullUrl.startsWith('http://') && !fullUrl.startsWith('https://')) {
            fullUrl = fullUrl.startsWith('/') ? baseURL + fullUrl : `${baseURL}/${fullUrl}`
          }
          // 尝试从后端获取原始文件名
          let originalFileName = fullUrl.split('/').pop() || '附件'
          try {
            const fileNameRes = await getFileName(fullUrl)
            if (fileNameRes && fileNameRes.code === '000000') {
              originalFileName = fileNameRes.data || fileNameRes.msg || originalFileName
            }
          } catch (error) {
            console.debug('获取文件名失败:', error)
          }
          return { 
            url: fullUrl, 
            fileName: originalFileName,
            originalFileName,
            size: 0 
          }
        }))
      }
    }
  }
  
  businessTripAttachments.value = attachments
}

// 处理返货单附件数据
const processReturnOrderAttachments = async (data) => {
  returnOrderAttachments.value = []
  if (!data || !data.attachments) return
  
  const baseURL = process.env.VUE_APP_BASE_API || 'http://localhost:8086'
  let attachments = []
  
  // 优先使用 attachmentInfos（包含文件大小信息）
  if (data.attachmentInfos && Array.isArray(data.attachmentInfos) && data.attachmentInfos.length > 0) {
    attachments = data.attachmentInfos.map(att => {
      let url = att.fileUrl || att.url || att
      if (typeof url === 'string' && !url.startsWith('http://') && !url.startsWith('https://')) {
        url = url.startsWith('/') ? baseURL + url : `${baseURL}/${url}`
      }
      return {
        url,
        fileName: att.fileName || url.split('/').pop() || '附件',
        originalFileName: att.originalFileName || att.fileName || url.split('/').pop() || '附件',
        size: att.size || 0
      }
    })
  } else if (data.attachments) {
    if (Array.isArray(data.attachments)) {
      attachments = await Promise.all(data.attachments.map(async (att) => {
        if (typeof att === 'string') {
          let url = att
          if (!url.startsWith('http://') && !url.startsWith('https://')) {
            url = url.startsWith('/') ? baseURL + url : `${baseURL}/${url}`
          }
          // 尝试从后端获取原始文件名和文件大小
          let originalFileName = url.split('/').pop() || '附件'
          let fileSize = 0
          try {
            const fileNameRes = await getFileName(url)
            if (fileNameRes && fileNameRes.code === '000000') {
              originalFileName = fileNameRes.data || fileNameRes.msg || originalFileName
            }
          } catch (error) {
            console.debug('获取文件名失败:', error)
          }
          // 尝试通过 HEAD 请求获取文件大小
          try {
            const response = await fetch(url, { method: 'HEAD' })
            if (response.ok) {
              const contentLength = response.headers.get('Content-Length')
              if (contentLength) {
                fileSize = parseInt(contentLength, 10) || 0
              }
            }
          } catch (error) {
            console.debug('获取文件大小失败:', error)
          }
          return { 
            url, 
            fileName: originalFileName, // 使用原始文件名
            originalFileName, // 保存原始文件名
            size: fileSize 
          }
        }
        return att
      }))
    } else if (typeof data.attachments === 'string') {
      try {
        const parsed = JSON.parse(data.attachments)
        const urls = Array.isArray(parsed) ? parsed : [parsed]
        attachments = await Promise.all(urls.map(async (url) => {
          if (typeof url === 'string') {
            let fullUrl = url
            if (!fullUrl.startsWith('http://') && !fullUrl.startsWith('https://')) {
              fullUrl = fullUrl.startsWith('/') ? baseURL + fullUrl : `${baseURL}/${fullUrl}`
            }
            // 尝试从后端获取原始文件名和文件大小
            let originalFileName = fullUrl.split('/').pop() || '附件'
            let fileSize = 0
            try {
              const fileNameRes = await getFileName(fullUrl)
              if (fileNameRes && fileNameRes.code === '000000') {
                originalFileName = fileNameRes.data || fileNameRes.msg || originalFileName
              }
            } catch (error) {
              console.debug('获取文件名失败:', error)
            }
            // 尝试通过 HEAD 请求获取文件大小
            try {
              const response = await fetch(fullUrl, { method: 'HEAD' })
              if (response.ok) {
                const contentLength = response.headers.get('Content-Length')
                if (contentLength) {
                  fileSize = parseInt(contentLength, 10) || 0
                }
              }
            } catch (error) {
              console.debug('获取文件大小失败:', error)
            }
            return { 
              url: fullUrl, 
              fileName: originalFileName, // 使用原始文件名
              originalFileName, // 保存原始文件名
              size: fileSize 
            }
          }
          return url
        }))
      } catch {
        const urlArray = data.attachments.split(',').map(s => s.trim()).filter(Boolean)
        attachments = await Promise.all(urlArray.map(async (url) => {
          let fullUrl = url
          if (!fullUrl.startsWith('http://') && !fullUrl.startsWith('https://')) {
            fullUrl = fullUrl.startsWith('/') ? baseURL + fullUrl : `${baseURL}/${fullUrl}`
          }
          // 尝试从后端获取原始文件名
          let originalFileName = fullUrl.split('/').pop() || '附件'
          try {
            const fileNameRes = await getFileName(fullUrl)
            if (fileNameRes && fileNameRes.code === '000000') {
              originalFileName = fileNameRes.data || fileNameRes.msg || originalFileName
            }
          } catch (error) {
            console.debug('获取文件名失败:', error)
          }
          return { 
            url: fullUrl, 
            fileName: originalFileName, // 使用原始文件名
            originalFileName, // 保存原始文件名
            size: 0 
          }
        }))
      }
    }
  }
  
  returnOrderAttachments.value = attachments
}

// 判断附件是否为图片
const isAttachmentImage = (attachment) => {
  if (!attachment) return false
  const fileName = getAttachmentFileName(attachment)
  return isImageFile(fileName)
}

// 获取附件文件名（优先显示原始文件名）
const getAttachmentFileName = (attachment) => {
  if (!attachment) return '未知文件'
  // 如果是对象，优先使用原始文件名
  if (typeof attachment === 'object') {
    if (attachment.originalFileName) {
      return attachment.originalFileName
    }
    if (attachment.fileName) {
      return attachment.fileName
    }
    if (attachment.name) {
      return attachment.name
    }
    if (attachment.url) {
      const parts = attachment.url.split('/')
      const fileName = parts[parts.length - 1].split('?')[0]
      return fileName || '附件'
    }
  }
  // 如果是字符串URL
  if (typeof attachment === 'string') {
    const parts = attachment.split('/')
    const fileName = parts[parts.length - 1].split('?')[0]
    return fileName || '附件'
  }
  return '未知文件'
}

// 获取附件URL
const getAttachmentUrl = (attachment) => {
  let url = ''
  if (typeof attachment === 'string') {
    url = attachment
  } else if (attachment && attachment.url) {
    url = attachment.url
  } else {
    return ''
  }
  
  // 如果是blob URL，直接返回（不需要代理）
  if (url.startsWith('blob:')) {
    return url
  }
  
  // 对于COS URL，使用代理接口（解决403问题）
  return getPreviewUrl(url)
}

// 获取附件大小
const getAttachmentSize = (attachment) => {
  if (!attachment) return 0
  if (typeof attachment === 'object' && attachment.size) {
    return attachment.size
  }
  return 0
}

// 获取所有图片的预览列表（用于 el-image 的预览功能）
const getImagePreviewList = () => {
  if (!returnOrderAttachments.value || !returnOrderAttachments.value.length) return []
  return returnOrderAttachments.value
    .filter(att => isAttachmentImage(att) && getAttachmentUrl(att))
    .map(att => getAttachmentUrl(att))
}

// 获取成交台账所有图片的预览列表
// eslint-disable-next-line no-unused-vars
const getDealImagePreviewList = () => {
  if (!dealAttachments.value || !dealAttachments.value.length) return []
  return dealAttachments.value
    .filter(att => isAttachmentImage(att) && getAttachmentUrl(att))
    .map(att => getAttachmentUrl(att))
}

// 获取报销申请所有图片的预览列表
const getTravelExpenseImagePreviewList = () => {
  if (!travelExpenseAttachments.value || !travelExpenseAttachments.value.length) return []
  return travelExpenseAttachments.value
    .filter(att => isAttachmentImage(att) && getAttachmentUrl(att))
    .map(att => getAttachmentUrl(att))
}

// 获取出差申请所有图片的预览列表
const getBusinessTripImagePreviewList = () => {
  if (!businessTripAttachments.value || !businessTripAttachments.value.length) return []
  return businessTripAttachments.value
    .filter(att => isAttachmentImage(att) && getAttachmentUrl(att))
    .map(att => getAttachmentUrl(att))
}

// 获取当前图片在预览列表中的索引
const getImageIndex = (currentIndex) => {
  if (!returnOrderAttachments.value || !returnOrderAttachments.value.length) return 0
  let imageIndex = 0
  for (let i = 0; i < currentIndex; i++) {
    const att = returnOrderAttachments.value[i]
    if (att && isAttachmentImage(att) && getAttachmentUrl(att)) {
      imageIndex++
    }
  }
  return imageIndex
}

// 获取成交台账当前图片在预览列表中的索引
// eslint-disable-next-line no-unused-vars
const getDealImageIndex = (currentIndex) => {
  if (!dealAttachments.value || !dealAttachments.value.length) return 0
  let imageIndex = 0
  for (let i = 0; i < currentIndex; i++) {
    const att = dealAttachments.value[i]
    if (att && isAttachmentImage(att) && getAttachmentUrl(att)) {
      imageIndex++
    }
  }
  return imageIndex
}

// 获取报销申请当前图片在预览列表中的索引
const getTravelExpenseImageIndex = (currentIndex) => {
  if (!travelExpenseAttachments.value || !travelExpenseAttachments.value.length) return 0
  let imageIndex = 0
  for (let i = 0; i < currentIndex; i++) {
    const att = travelExpenseAttachments.value[i]
    if (att && isAttachmentImage(att) && getAttachmentUrl(att)) {
      imageIndex++
    }
  }
  return imageIndex
}

// 获取出差申请当前图片在预览列表中的索引
const getBusinessTripImageIndex = (currentIndex) => {
  if (!businessTripAttachments.value || !businessTripAttachments.value.length) return 0
  let imageIndex = 0
  for (let i = 0; i < currentIndex; i++) {
    const att = businessTripAttachments.value[i]
    if (att && isAttachmentImage(att) && getAttachmentUrl(att)) {
      imageIndex++
    }
  }
  return imageIndex
}

// 处理图片加载错误
const handleImageError = (event) => {
  if (event.target) {
    event.target.style.display = 'none'
  }
}

// 获取文件扩展名
const getFileExtension = (attachment) => {
  const fileName = getAttachmentFileName(attachment)
  if (!fileName) return ''
  const parts = fileName.split('.')
  return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : ''
}

// 获取文件类型图标
const getFileIcon = (attachment) => {
  const ext = getFileExtension(attachment)
  // 图片类型
  if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(ext)) {
    return Picture
  }
  // 视频类型
  if (['mp4', 'avi', 'mov', 'wmv', 'flv'].includes(ext)) {
    return VideoPlay
  }
  // 其他类型使用文档图标
  return Document
}

// 获取文件类型标签（显示在图标上）
const getFileTypeLabel = (attachment) => {
  const ext = getFileExtension(attachment)
  const labelMap = {
    'xls': 'XLS',
    'xlsx': 'XLSX',
    'doc': 'DOC',
    'docx': 'DOCX',
    'pdf': 'PDF',
    'txt': 'TXT',
    'ppt': 'PPT',
    'pptx': 'PPTX',
    'zip': 'ZIP',
    'rar': 'RAR',
    '7z': '7Z'
  }
  return labelMap[ext] || ''
}

// 获取文件类型图标样式类
const getFileIconClass = (attachment) => {
  const ext = getFileExtension(attachment)
  const iconClassMap = {
    // Excel
    'xls': 'icon-excel',
    'xlsx': 'icon-excel',
    // Word
    'doc': 'icon-word',
    'docx': 'icon-word',
    // PDF
    'pdf': 'icon-pdf',
    // 文本
    'txt': 'icon-txt',
    // 图片
    'jpg': 'icon-image',
    'jpeg': 'icon-image',
    'png': 'icon-image',
    'gif': 'icon-image',
    'bmp': 'icon-image',
    'webp': 'icon-image',
    // PPT
    'ppt': 'icon-ppt',
    'pptx': 'icon-ppt',
    // 压缩包
    'zip': 'icon-zip',
    'rar': 'icon-zip',
    '7z': 'icon-zip'
  }
  return iconClassMap[ext] || 'icon-default'
}

// 下载附件
const handleDownloadAttachment = async (attachment) => {
  let url = typeof attachment === 'string' ? attachment : attachment.url
  let fileName = typeof attachment === 'string' ? undefined : (attachment.originalFileName || attachment.fileName)
  
  if (!url) {
    ElMessage.warning('附件地址无效')
    return
  }
  
  try {
    await downloadFileFromServer(url, fileName)
  } catch (error) {
    console.error('下载附件失败:', error)
    ElMessage.error(error.message || '下载附件失败')
  }
}

// 监听业务类型和ID变化
watch(
  () => [props.businessType, props.businessId],
  () => {
    // 清空之前的数据
    dealDetail.value = null
    dealAttachments.value = []
    contractDetail.value = null
    returnOrderDetail.value = null
    returnOrderAttachments.value = []
    travelExpenseDetail.value = null
    travelExpenseAttachments.value = []
    businessTripDetail.value = null
    businessTripAttachments.value = []
    
    // 加载新的业务详情
    loadBusinessDetail()
  },
  { immediate: true }
)

onMounted(() => {
  loadBusinessDetail()
})
</script>

<style scoped>
.business-detail {
  width: 100%;
}

.customer-name,
.contract-number,
.order-number,
.expense-title,
.trip-title {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
}

.amount,
.commission {
  color: #52c41a;
  font-weight: 600;
}

.content-text,
.attachment-text {
  word-break: break-all;
  white-space: pre-wrap;
  line-height: 1.6;
  max-height: 200px;
  overflow-y: auto;
  padding: 8px;
  background: #f5f7fa;
  border-radius: 4px;
}

.attachment-text {
  font-size: 12px;
  color: #606266;
}

.no-detail {
  padding: 40px 0;
  text-align: center;
}

/* 优化描述列表的间距 */
:deep(.el-descriptions) {
  margin-bottom: 0;
}

:deep(.el-descriptions__label) {
  font-weight: 500;
  color: #606266;
  width: 120px;
}

:deep(.el-descriptions__content) {
  color: #303133;
}

/* 附件列表样式 */
.attachment-list {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.attachment-item {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  background-color: #f8f9fa;
  border: 1px solid #e4e7ed;
  border-radius: 8px;
  transition: all 0.3s;
  width: calc(50% - 6px);
  box-sizing: border-box;
}

.attachment-item:hover {
  background-color: #f0f2f5;
  border-color: #c0c4cc;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

/* 图片附件样式 */
.attachment-item-image {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 14px;
  border: 1px solid #e4e7ed;
  background-color: #ffffff;
  width: calc(50% - 6px);
  box-sizing: border-box;
}

.attachment-item-image:hover {
  border-color: #c0c4cc;
}

.attachment-image-wrapper {
  position: relative;
  width: 100px;
  height: 100px;
  margin-bottom: 8px;
  flex-shrink: 0;
  border-radius: 8px;
  overflow: hidden;
  background-color: #f5f7fa;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
}

.attachment-image-wrapper:hover {
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
  transform: scale(1.02);
}

.attachment-image-preview {
  width: 100%;
  height: 100%;
  object-fit: cover;
  cursor: pointer;
  transition: transform 0.3s ease;
}

.attachment-image-preview:hover {
  transform: scale(1.05);
}

.image-slot {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  background: #f5f7fa;
  color: #909399;
  font-size: 20px;
}

.download-overlay {
  position: absolute;
  bottom: 4px;
  right: 4px;
  opacity: 1;
  z-index: 3;
}

.download-overlay-btn {
  background-color: rgba(64, 158, 255, 0.9);
  border: none;
  color: #fff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

.download-overlay-btn:hover {
  background-color: #409eff;
  box-shadow: 0 4px 12px rgba(64, 158, 255, 0.4);
}

.attachment-image-info {
  flex: 1;
  width: 100%;
}

.attachment-image-details {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.attachment-icon-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  margin-right: 12px;
  flex-shrink: 0;
  border-radius: 6px;
  background-color: #f5f7fa;
  position: relative;
}

.attachment-icon {
  font-size: 24px;
}

.file-type-label {
  position: absolute;
  bottom: 2px;
  left: 0;
  right: 0;
  font-size: 9px;
  font-weight: 600;
  text-align: center;
  line-height: 1;
  color: #fff;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
}

.attachment-name {
  flex: 1;
  font-size: 14px;
  color: #303133;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-right: 12px;
}

.attachment-size {
  font-size: 12px;
  color: #909399;
  margin-right: 12px;
  flex-shrink: 0;
  min-width: 50px;
}

.attachment-actions {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
}

/* 文件类型图标样式 */
.icon-excel {
  background: linear-gradient(135deg, #1d6f42 0%, #217346 100%);
}

.icon-excel .attachment-icon {
  color: #fff;
}

.icon-word {
  background: linear-gradient(135deg, #2b579a 0%, #3b6cb0 100%);
}

.icon-word .attachment-icon {
  color: #fff;
}

.icon-pdf {
  background: linear-gradient(135deg, #f40f02 0%, #ff1a0a 100%);
}

.icon-pdf .attachment-icon {
  color: #fff;
}

.icon-txt {
  background: linear-gradient(135deg, #666666 0%, #808080 100%);
}

.icon-txt .attachment-icon {
  color: #fff;
}

.icon-image {
  background: linear-gradient(135deg, #4a90e2 0%, #5ba3f5 100%);
}

.icon-image .attachment-icon {
  color: #fff;
}

.icon-default {
  background: linear-gradient(135deg, #909399 0%, #a6a9ad 100%);
}

.icon-default .attachment-icon {
  color: #fff;
}

/* 返货明细表格样式 */
.return-order-items-section {
  margin-top: 20px;
}

.items-section-title {
  margin: 0 0 12px 0;
  font-size: 14px;
  font-weight: 600;
  color: #303133;
}

.return-order-items-section :deep(.el-table) {
  font-size: 13px;
}

.return-order-items-section :deep(.el-table th) {
  background-color: #f5f7fa;
  font-weight: 600;
  color: #606266;
}

.return-order-items-section :deep(.el-table td) {
  padding: 8px 0;
}

/* 附件区域样式（独立展示，避免在 el-descriptions 中导致 ResizeObserver 错误） */
.deal-attachments-section,
.return-order-attachments-section,
.travel-expense-attachments-section,
.business-trip-attachments-section {
  margin-top: 20px;
  padding: 16px;
  background-color: #fafafa;
  border-radius: 4px;
  border: 1px solid #e4e7ed;
}

.attachments-section-title {
  margin: 0 0 12px 0;
  font-size: 14px;
  font-weight: 600;
  color: #303133;
}
</style>
